import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, Home, Paintbrush2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Gate 2 · S4 (2026-02) — Painter-facing read-only viewer for a homeowner's
 * shared visualisation. Route: /pro/leads/shared/:token. All three data
 * calls (summary + photo + composite) are auth-gated to the painter who
 * owns the share on the server side.
 */
export default function PainterSharedVisualisation() {
  const { token } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [compositeUrl, setCompositeUrl] = useState(null);

  // Load summary
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.get(`/painter/leads/shared/${token}/summary`)
      .then((r) => { if (!cancelled) setSummary(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        setError(status === 404
          ? "This shared visualisation isn't available. It may have been revoked."
          : formatApiError(e?.response?.data?.detail) || "Could not load the visualisation.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  // Load images (blobs) as separate effects so a missing composite doesn't block the photo.
  useEffect(() => {
    if (!summary) return undefined;
    let dead = false; let url = null;
    api.get(`/painter/leads/shared/${token}/photo`, { responseType: "blob" })
      .then((r) => { if (dead) return; url = URL.createObjectURL(r.data); setPhotoUrl(url); })
      .catch(() => {});
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [summary, token]);

  useEffect(() => {
    if (!summary?.has_composite) return undefined;
    let dead = false; let url = null;
    api.get(`/painter/leads/shared/${token}/composite`, { responseType: "blob" })
      .then((r) => { if (dead) return; url = URL.createObjectURL(r.data); setCompositeUrl(url); })
      .catch(() => {});
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
  }, [summary, token]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-brand-muted" data-testid="shared-vis-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading visualisation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="shared-vis-error">
          <h1 className="font-serif text-2xl text-brand-ink mb-3">Visualisation unavailable</h1>
          <p className="text-sm text-brand-muted mb-6">{error}</p>
          <Link to="/pro/leads" className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90">
            <ArrowLeft size={14} /> Back to Leads
          </Link>
        </div>
      </div>
    );
  }

  const mailtoHref = summary?.homeowner?.email ? `mailto:${encodeURIComponent(summary.homeowner.email)}` : null;
  const copyEmail = async () => {
    if (!summary?.homeowner?.email) return;
    try {
      await navigator.clipboard.writeText(summary.homeowner.email);
      toast.success("Homeowner email copied");
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-8 cm-scroll" data-testid="shared-vis-page">
      {/* Header */}
      <div>
        <Link to="/pro/leads" className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink mb-4" data-testid="shared-vis-back">
          <ArrowLeft size={13} /> Back to Leads
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="overline text-brand-teal mb-2 inline-flex items-center gap-1.5">
              <Home size={11} /> Homeowner Shared
            </div>
            <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink">
              {summary?.project?.title || "Shared visualisation"}
            </h1>
            <p className="text-sm text-brand-muted mt-2 max-w-2xl leading-relaxed">
              <span className="font-medium text-brand-ink">{summary?.homeowner?.name}</span>
              {" "}shared their ColourMatch project with you on{" "}
              {summary?.shared_at
                ? new Date(summary.shared_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
                : "an earlier date"}.
              {summary?.homeowner_note && (
                <>{" "}They left this note: <em className="text-brand-ink">{summary.homeowner_note}</em></>
              )}
            </p>
          </div>
          <div className="shrink-0 flex flex-wrap gap-2">
            {mailtoHref && (
              <a
                href={mailtoHref}
                onClick={copyEmail}
                data-testid="shared-vis-reply-email"
                className="inline-flex items-center gap-2 border border-brand-navy text-brand-navy h-10 px-4 rounded-2xl text-sm font-semibold hover:bg-brand-sand"
              >
                <Mail size={14} /> Reply by email
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Photo + Composite side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="shared-vis-images">
        <ImageCard label="Original photo" src={photoUrl} testId="shared-vis-original" empty="Homeowner hasn't uploaded a photo yet." />
        <ImageCard
          label={summary?.has_composite ? "Their painted scheme" : "Painted scheme"}
          src={compositeUrl}
          testId="shared-vis-composite"
          empty={summary?.has_composite ? "Composite loading…" : "The homeowner hasn't rendered a scheme yet."}
        />
      </div>

      {/* Colour list */}
      <div className="border border-brand-linen bg-white rounded-2xl overflow-hidden shadow-soft" data-testid="shared-vis-colours">
        <div className="px-6 pt-5 pb-3 border-b border-brand-linen flex items-center gap-2">
          <Paintbrush2 size={14} className="text-brand-teal" />
          <span className="overline">Colours the homeowner chose</span>
          <span className="ml-auto text-[11px] text-brand-muted">
            {summary?.assignments?.length || 0} surface{(summary?.assignments?.length || 0) === 1 ? "" : "s"}
          </span>
        </div>
        {(summary?.assignments?.length || 0) === 0 ? (
          <div className="p-6 text-sm text-brand-muted">No colour choices recorded yet.</div>
        ) : (
          <ul className="divide-y divide-brand-linen">
            {summary.assignments.map((a, i) => (
              <li key={`${a.surface}-${i}`} className="px-6 py-3 flex items-center gap-3">
                <span
                  className="inline-block w-7 h-7 rounded-lg border border-brand-linen flex-shrink-0"
                  style={{ background: a.color_hex || "#e7e3d8" }}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-brand-ink">{a.color_name || a.color_hex || "—"}</div>
                  <div className="text-xs text-brand-muted capitalize">
                    {[a.surface, a.finish].filter(Boolean).join(" · ").replace(/_/g, " ")}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">{a.color_hex}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ImageCard({ label, src, testId, empty }) {
  return (
    <div className="border border-brand-linen bg-white rounded-2xl overflow-hidden shadow-soft flex flex-col" data-testid={testId}>
      <div className="px-5 py-3 border-b border-brand-linen">
        <span className="overline">{label}</span>
      </div>
      <div className="flex-1 min-h-[240px] flex items-center justify-center bg-brand-linen/30 p-4">
        {src ? (
          <img src={src} alt={label} className="max-h-[420px] max-w-full object-contain rounded-lg" />
        ) : (
          <span className="text-xs text-brand-muted">{empty}</span>
        )}
      </div>
    </div>
  );
}
